import React, { useRef, useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTrainingStore } from "../../store/store";
import { Trainee, Test } from "../../types/api";
import type { ColDef } from "ag-grid-community";
import { useTrainingModule } from "./TrainingModuleContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface TraineeRow {
  id: string;
  name: string;
  preEvaluation: string;
  postEvaluation: string;
  postTestAttendance: "Present" | "Absent";
}

const PastEvaluationDetails = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { id } = useParams<{ id: string }>();
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { fetchTrainings, getTestByTrainingId, trainings, tests, loading, error } = useTrainingStore();
  const scopedTrainings = useMemo(
    () =>
      trainings.filter(
        (training) =>
          training?.moduleType === moduleType ||
          (isFixedCategory && training?.category === validCategories[0]),
      ),
    [trainings, moduleType, isFixedCategory, validCategories],
  );
  const [training, setTraining] = useState<any | null>(null);
  const [rowData, setRowData] = useState<TraineeRow[]>([]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Legacy trainings only ever persisted total minutes, so synthesize a
  // range from a default 09:00 start instead of showing a bare minute count.
  const DEFAULT_TRAINING_START_TIME = "09:00";
  const formatTrainingTime = (duration: string | undefined): string => {
    const total = parseInt(duration || "0", 10) || 0;
    if (!total) return "-";
    const [h, m] = DEFAULT_TRAINING_START_TIME.split(":").map(Number);
    const endMinutes = (((h * 60 + m + total) % 1440) + 1440) % 1440;
    const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(
      endMinutes % 60,
    ).padStart(2, "0")}`;
    return `${DEFAULT_TRAINING_START_TIME}-${endTime}`;
  };

  const formatScore = (score: number | undefined): string => {
    return score !== undefined ? `${Math.round(score)}%` : "-";
  };

  const categorizeScores = (testCategory: "Pre-Test" | "Post-Test") => {
    const relevantTests = tests.filter((test) => test.trainingId === id && test.testCategory === testCategory);
    const scores = relevantTests
      .flatMap((test) =>
        (test.results || []).map((result) => {
          const score = result.score || 0;
          return score;
        })
      );

    const totalTrainees = scores.length;
    const categories = {
      Poor: scores.filter((s) => s < 25).length,
      Average: scores.filter((s) => s >= 25 && s < 50).length,
      Good: scores.filter((s) => s >= 50 && s < 75).length,
      Excellent: scores.filter((s) => s >= 75 && s <= 100).length,
    };

    return {
      Poor: categories.Poor,
      Average: categories.Average,
      Good: categories.Good,
      Excellent: categories.Excellent,
    };
  };

  const evaluationData = {
    labels: ["Poor", "Average", "Good", "Excellent"],
    datasets: [
      {
        label: "Pre-Evaluation",
        data: [
          categorizeScores("Pre-Test").Poor,
          categorizeScores("Pre-Test").Average,
          categorizeScores("Pre-Test").Good,
          categorizeScores("Pre-Test").Excellent,
        ],
        backgroundColor: "#68a7ba",
        borderRadius: 4,
        barThickness: 15,
      },
      {
        label: "Post-Evaluation",
        data: [
          categorizeScores("Post-Test").Poor,
          categorizeScores("Post-Test").Average,
          categorizeScores("Post-Test").Good,
          categorizeScores("Post-Test").Excellent,
        ],
        backgroundColor: "#7ecb89",
        borderRadius: 4,
        barThickness: 15,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" as const },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";
            const value = context.raw as number;
            const range = (() => {
              switch (context.label) {
                case "Poor": return "0-25";
                case "Average": return "25-50";
                case "Good": return "50-75";
                case "Excellent": return "75-100";
                default: return "";
              }
            })();
            return `${label}: ${value} Trainees, Score: ${range}%`;
          },
        },
      },
      title: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#333", font: { size: 11 }, maxRotation: 0, minRotation: 0, maxTicksLimit: 4, padding: 10, autoSkip: false },
        border: { display: true, color: "rgba(0, 0, 0, 0.2)" }
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 25,
          color: "#333",
          font: { size: 12 },
          callback: (tickValue: string | number) => `${tickValue}`
        },
        grid: { color: "rgba(0, 0, 0, 0.1)", drawBorder: false },
        border: { display: false },
        title: { display: true, text: "No. of Trainees", color: "#333", font: { size: 15 } }
      },
    },
  };

  const calculateAttendance = (trainees: Trainee[]) => {
    if (!trainees || trainees.length === 0) return "0/0";
    const presentCount = trainees.filter((trainee) => trainee.attendance?.["Post-Test"] === "Present").length;
    return `${presentCount}/${trainees.length}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        await fetchTrainings({
          where: isFixedCategory
            ? { id, moduleType, category: validCategories[0] }
            : { id, moduleType },
        });
        await getTestByTrainingId(id);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, [id, fetchTrainings, getTestByTrainingId]);

  useEffect(() => {
    const selectedTraining = scopedTrainings.find((t) => t.id === id);
    if (selectedTraining) {
      setTraining(selectedTraining);

      const preTest = tests.find((test) => test.trainingId === id && test.testCategory === "Pre-Test");
      const postTest = tests.find((test) => test.trainingId === id && test.testCategory === "Post-Test");

      const traineesData: TraineeRow[] = selectedTraining.trainees.map((trainee: Trainee) => {
        const preTestResult = preTest?.results?.find((result) => result.traineeId === trainee.traineeId);
        const postTestResult = postTest?.results?.find((result) => result.traineeId === trainee.traineeId);
        return {
          id: trainee.traineeEmployeeId || "-",
          name: trainee.name,
          preEvaluation: formatScore(preTestResult?.score),
          postEvaluation: formatScore(postTestResult?.score),
          postTestAttendance: trainee.attendance?.["Post-Test"] || "Absent",
        };
      });
      setRowData(traineesData);
    }
  }, [scopedTrainings, tests, id]);

  const columnDefs: ColDef<TraineeRow>[] = [
    { headerName: "ID", field: "id", flex: 1 },
    { headerName: "Name", field: "name", flex: 1 },
    { headerName: "Pre Evaluation", field: "preEvaluation", flex: 1 },
    { headerName: "Post Evaluation", field: "postEvaluation", flex: 1 },
    {
      headerName: "Post-Test Attendance",
      field: "postTestAttendance",
      flex: 1,
      cellRenderer: (params: { value: "Present" | "Absent" }) => (
        <div>
          {params.value === "Present" ? (
            <span className="text-green-500">✔</span>
          ) : (
            <span className="text-red-500">✘</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Past Evaluation Details</h2>
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <Link to={paths.pastEvaluations}>
            <button className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white whitespace-nowrap">
              <ArrowLeft className="mr-2 h-4 w-4" />
              BACK
            </button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : training ? (
        <div className="flex justify-between items-center gap-4 mb-4 bg-white px-4 py-3 shadow-xl">
          <div>
            <p className="text-lg text-black font-bold">Topic</p>
            <p className="font-medium text-black">{training.topic}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Category</p>
            <p className="font-medium text-black">{training.category}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Date</p>
            <p className="font-medium text-black">{formatDate(training.date)}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Training Time</p>
            <p className="font-medium text-black">
              {training.startTime && training.endTime
                ? `${training.startTime}-${training.endTime}`
                : formatTrainingTime(training.duration)}
            </p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Post-Test Attendance</p>
            <p className="font-medium text-black">{calculateAttendance(training.trainees)}</p>
          </div>
          <div>
            <p className="text-lg text-black font-bold">Total Trainees</p>
            <p className="font-medium text-black">{training.trainees.length}</p>
          </div>
        </div>
      ) : (
        <div>No training data found</div>
      )}

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="ag-theme-alpine flex-1">
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            rowHeight={50}
            headerHeight={65}
            paginationPageSize={20}
            pagination={true}
            domLayout="autoHeight"
            defaultColDef={{ sortable: true, resizable: true }}
            getRowStyle={() => ({
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              marginBottom: "8px",
              backgroundColor: "white",
            })}
          />
        </div>

        <div className="bg-white p-4 shadow-xl rounded w-full lg:w-1/3">
          <h3 className="text-lg font-bold mb-4 text-center">Evaluation</h3>
          <div style={{ height: "400px", position: "relative" }}>
            <Bar data={evaluationData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastEvaluationDetails;